import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import {
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Tooltip,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import {
  CtyButton,
  CtyShowToast,
  CtySpinner,
  HORIZONTAL_RULES_AG_GRID_CSS,
  SimpleDeleteConfirmationModal,
  checkType,
  isSorAuthError,
} from '@circuitry-ai/doc-ui';
import InspectionTemplateEditModal from './InspectionTemplateEditModal';
import type { InspectionTemplate } from './inspection-templates/inspection-templates-types';
import { useInspectionTemplatesApi } from './inspection-templates/useInspectionTemplatesApi';
import { getSorErrorMessage } from './work-queues/useWorkQueuesApi';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

type LoadError = { message: string; needsConnect: boolean };

interface InspectionTemplateGridContext {
  onEdit: (template: InspectionTemplate) => void;
  onDelete: (template: InspectionTemplate) => void;
  onDuplicate: (template: InspectionTemplate) => void;
  onNameChange: (template: InspectionTemplate, name: string) => void;
  onTypeChange: (template: InspectionTemplate, type: string) => void;
  onSave: (template: InspectionTemplate) => void;
  draftNames: Record<string, string>;
  draftTypes: Record<string, string>;
  savingTemplateId: string | null;
  duplicatingTemplateId: string | null;
}

function hasNameChange(template: InspectionTemplate, draftNames: Record<string, string>): boolean {
  const draftName = draftNames[template.id];
  return draftName !== undefined
    && draftName.trim().length > 0
    && draftName.trim() !== template.name;
}

function hasTypeChange(template: InspectionTemplate, draftTypes: Record<string, string>): boolean {
  const draftType = draftTypes[template.id];
  return draftType !== undefined && draftType.trim() !== template.type;
}

function TemplateNameCell({
  data,
  context,
}: Readonly<ICellRendererParams<InspectionTemplate>>) {
  const gridContext = context as InspectionTemplateGridContext | undefined;
  const savedName = data?.name ?? '';
  // AG Grid never re-renders a cell renderer when the grid `context` changes, so the
  // input has to own its own value and only re-sync when the saved name changes.
  const [value, setValue] = useState(savedName);
  useEffect(() => setValue(savedName), [savedName]);

  if (!data || !gridContext) return null;

  return (
    <input
      value={value}
      maxLength={200}
      aria-label={`Template name for ${savedName}`}
      className="h-8 w-full rounded-md border border-transparent bg-transparent px-2 text-sm font-semibold text-foreground outline-none hover:border-default-200 focus:border-primary"
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => {
        setValue(event.target.value);
        gridContext.onNameChange(data, event.target.value);
      }}
    />
  );
}

function TemplateTypeCell({
  data,
  context,
}: Readonly<ICellRendererParams<InspectionTemplate>>) {
  const gridContext = context as InspectionTemplateGridContext | undefined;
  const savedType = data?.type ?? '';
  const [value, setValue] = useState(savedType);
  useEffect(() => setValue(savedType), [savedType]);

  if (!data || !gridContext) return null;

  return (
    <input
      value={value}
      maxLength={100}
      aria-label={`Template type for ${data.name}`}
      className="h-8 w-full rounded-md border border-default-200 bg-content1 px-2 text-sm text-foreground outline-none focus:border-primary"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        gridContext.onSave(data);
      }}
      onChange={(event) => {
        setValue(event.target.value);
        gridContext.onTypeChange(data, event.target.value);
      }}
    />
  );
}

function ItemsCell({ data }: Readonly<ICellRendererParams<InspectionTemplate>>) {
  if (!data) return null;
  if (data.itemCount === null) {
    return <span className="text-sm text-default-400">—</span>;
  }
  const itemCount = data.itemCount;
  const requiredCount = data.requiredItemCount ?? 0;

  return (
    <div className="flex h-full items-center gap-2">
      <Chip size="sm" variant="flat" color="default">
        {itemCount} {itemCount === 1 ? 'item' : 'items'}
      </Chip>
      {requiredCount > 0 && (
        <Chip size="sm" variant="flat" color="default">
          {requiredCount} required
        </Chip>
      )}
    </div>
  );
}

function ActionsCell({ data, context }: Readonly<ICellRendererParams<InspectionTemplate>>) {
  const { t } = useTranslation();
  const gridContext = context as InspectionTemplateGridContext | undefined;
  if (!data || !gridContext) return null;
  const nameChanged = hasNameChange(data, gridContext.draftNames);
  const typeChanged = hasTypeChange(data, gridContext.draftTypes);
  const isSaving = gridContext.savingTemplateId === data.id;
  const isDuplicating = gridContext.duplicatingTemplateId === data.id;

  return (
    <div className="flex h-full items-center justify-end gap-1">
      {(nameChanged || typeChanged) && (
        <Tooltip content={t('Save Template')}>
          <button
            type="button"
            className="rounded-md p-1.5 text-primary hover:bg-primary/10 disabled:opacity-50"
            aria-label={t('Save Template')}
            disabled={isSaving}
            onClick={() => gridContext.onSave(data)}
          >
            <Icon icon={isSaving ? 'eos-icons:loading' : 'solar:diskette-linear'} width={17} />
          </button>
        </Tooltip>
      )}
      <Tooltip content={t('Edit Template')}>
        <button
          type="button"
          className="rounded-md p-1.5 text-default-500 hover:bg-default-100 hover:text-foreground"
          aria-label={t('Edit Template')}
          onClick={() => gridContext.onEdit(data)}
        >
          <Icon icon="solar:pen-2-linear" width={17} />
        </button>
      </Tooltip>
      <Tooltip content={t('Delete Template')}>
        <button
          type="button"
          className="rounded-md p-1.5 text-default-500 hover:bg-danger/10 hover:text-danger"
          aria-label={t('Delete Template')}
          onClick={() => gridContext.onDelete(data)}
        >
          <Icon icon="solar:trash-bin-trash-linear" width={17} />
        </button>
      </Tooltip>
      <Dropdown placement="bottom-end">
        <DropdownTrigger>
          <button
            type="button"
            className="rounded-md p-1.5 text-default-500 hover:bg-default-100 hover:text-foreground disabled:opacity-50"
            aria-label={t('Template actions')}
            disabled={isDuplicating}
          >
            <Icon icon={isDuplicating ? 'eos-icons:loading' : 'solar:menu-dots-bold'} width={17} />
          </button>
        </DropdownTrigger>
        <DropdownMenu
          aria-label={t('Template actions')}
          onAction={(key) => {
            if (key === 'duplicate') gridContext.onDuplicate(data);
          }}
        >
          <DropdownItem
            key="duplicate"
            startContent={<Icon icon="solar:copy-linear" width={16} />}
          >
            {t('Duplicate Template')}
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}

const COLUMN_DEFS: ColDef<InspectionTemplate>[] = [
  {
    headerName: 'Template Name',
    field: 'name',
    flex: 2,
    minWidth: 240,
    cellRenderer: TemplateNameCell,
  },
  {
    headerName: 'Type',
    field: 'type',
    flex: 1.2,
    minWidth: 180,
    cellRenderer: TemplateTypeCell,
  },
  {
    headerName: 'Items',
    colId: 'items',
    flex: 2,
    minWidth: 220,
    cellRenderer: ItemsCell,
    sortable: false,
  },
  {
    headerName: '',
    colId: 'actions',
    width: 140,
    minWidth: 140,
    maxWidth: 140,
    sortable: false,
    cellRenderer: ActionsCell,
    cellClass: 'text-right',
    headerClass: 'text-right',
  },
];

export default function InspectionTemplatesSettings() {
  const { t } = useTranslation();
  const {
    fetchTemplates,
    createTemplate,
    updateTemplate,
    duplicateTemplate,
    deleteTemplate,
    connectSor,
  } = useInspectionTemplatesApi();
  const gridRef = useRef<AgGridReact<InspectionTemplate>>(null);
  const [templates, setTemplates] = useState<InspectionTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<LoadError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [isConnecting, setIsConnecting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<InspectionTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState<InspectionTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [draftNames, setDraftNames] = useState<Record<string, string>>({});
  const [draftTypes, setDraftTypes] = useState<Record<string, string>>({});
  const [savingTemplateId, setSavingTemplateId] = useState<string | null>(null);
  const [duplicatingTemplateId, setDuplicatingTemplateId] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setLoadError(null);
    fetchTemplates(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setTemplates(rows);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.error('Failed to load inspection templates', error);
        setLoadError({
          message: getSorErrorMessage(error, t('Failed to load inspection templates')),
          needsConnect: isSorAuthError(error),
        });
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [fetchTemplates, reloadKey, t]);

  const reloadTemplates = useCallback(() => setReloadKey((key) => key + 1), []);

  // Runs from a click, so the Auth0 consent/login popup isn't blocked
  const handleConnect = useCallback(async () => {
    setIsConnecting(true);
    try {
      await connectSor();
      reloadTemplates();
    } catch (error) {
      setLoadError({
        message: getSorErrorMessage(error, t('Could not connect to the System of Record.')),
        needsConnect: true,
      });
    } finally {
      setIsConnecting(false);
    }
  }, [connectSor, reloadTemplates, t]);

  // The new template starts with no checklist items; the editor opens so the user adds them
  const handleCreate = useCallback(async () => {
    setIsCreating(true);
    try {
      const created = await createTemplate(`New Template ${Date.now()}`);
      CtyShowToast(checkType.success, t('{{name}} created successfully', { name: created.name }));
      reloadTemplates();
      setEditTarget(created);
    } catch (error) {
      CtyShowToast(checkType.error, getSorErrorMessage(error, t('Failed to create template')));
    } finally {
      setIsCreating(false);
    }
  }, [createTemplate, reloadTemplates, t]);

  const handleNameChange = useCallback((template: InspectionTemplate, name: string) => {
    setDraftNames((current) => ({ ...current, [template.id]: name }));
  }, []);

  const handleTypeChange = useCallback((template: InspectionTemplate, type: string) => {
    setDraftTypes((current) => ({ ...current, [template.id]: type }));
  }, []);

  const clearDrafts = useCallback((templateId: string) => {
    setDraftNames((current) => {
      const next = { ...current };
      delete next[templateId];
      return next;
    });
    setDraftTypes((current) => {
      const next = { ...current };
      delete next[templateId];
      return next;
    });
  }, []);

  const handleSave = useCallback(async (template: InspectionTemplate) => {
    const name = (draftNames[template.id] ?? template.name).trim();
    const type = (draftTypes[template.id] ?? template.type).trim();
    if (name.length < 2) {
      CtyShowToast(checkType.error, t('Template name must be at least 2 characters'));
      return;
    }
    if (name === template.name && type === template.type) return;

    setSavingTemplateId(template.id);
    try {
      await updateTemplate(template, { name, type });
      clearDrafts(template.id);
      CtyShowToast(checkType.success, t('Template updated successfully'));
      reloadTemplates();
    } catch (error) {
      CtyShowToast(checkType.error, getSorErrorMessage(error, t('Failed to update template')));
    } finally {
      setSavingTemplateId(null);
    }
  }, [clearDrafts, draftNames, draftTypes, reloadTemplates, t, updateTemplate]);

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  const onSave = useCallback((template: InspectionTemplate) => {
    void handleSaveRef.current(template);
  }, []);

  const handleDuplicate = useCallback(async (template: InspectionTemplate) => {
    setDuplicatingTemplateId(template.id);
    try {
      const copy = await duplicateTemplate(template);
      CtyShowToast(checkType.success, t('{{name}} created successfully', { name: copy.name }));
      reloadTemplates();
    } catch (error) {
      CtyShowToast(checkType.error, getSorErrorMessage(error, t('Failed to duplicate template')));
    } finally {
      setDuplicatingTemplateId(null);
    }
  }, [duplicateTemplate, reloadTemplates, t]);

  const gridContext = useMemo<InspectionTemplateGridContext>(() => ({
    onEdit: setEditTarget,
    onDelete: setDeleteTarget,
    onDuplicate: (template) => void handleDuplicate(template),
    onNameChange: handleNameChange,
    onTypeChange: handleTypeChange,
    onSave,
    draftNames,
    draftTypes,
    savingTemplateId,
    duplicatingTemplateId,
  }), [
    draftNames,
    draftTypes,
    duplicatingTemplateId,
    handleDuplicate,
    handleNameChange,
    handleTypeChange,
    onSave,
    savingTemplateId,
  ]);

  const pendingEditIds = useMemo(() => templates
    .filter((item) => hasNameChange(item, draftNames) || hasTypeChange(item, draftTypes))
    .map((item) => item.id)
    .join(','), [draftNames, draftTypes, templates]);

  // A grid `context` update alone never re-renders cell renderers, so the actions cell
  // would keep showing the pre-edit buttons. Refresh it when a row gains or loses a
  // pending name or type edit (not on every keystroke) so the save icon appears and disappears.
  useEffect(() => {
    gridRef.current?.api?.refreshCells({ columns: ['actions'], force: true });
  }, [duplicatingTemplateId, pendingEditIds, savingTemplateId]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteTemplate(deleteTarget);
      setDeleteTarget(null);
      CtyShowToast(checkType.success, t('Inspection template deleted successfully'));
      reloadTemplates();
    } catch (error) {
      CtyShowToast(
        checkType.error,
        getSorErrorMessage(error, t('Failed to delete inspection template')),
      );
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, deleteTemplate, reloadTemplates, t]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{t('Template Management')}</h1>
          <p className="mt-1 text-sm text-default-500">
            {t('Create templates and define the checklist items technicians must capture')}
          </p>
        </div>
        <CtyButton
          color="primary"
          size="sm"
          radius="sm"
          startContent={<Icon icon="solar:add-circle-linear" width={16} />}
          onPress={() => void handleCreate()}
          isLoading={isCreating}
          isDisabled={isLoading || isCreating || Boolean(loadError)}
        >
          {t('Create Template')}
        </CtyButton>
      </div>

      {loadError && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-danger bg-danger/10 px-4 py-3">
          <span className="text-sm text-danger">{loadError.message}</span>
          {loadError.needsConnect ? (
            <CtyButton
              size="sm"
              color="primary"
              onPress={() => void handleConnect()}
              isLoading={isConnecting}
            >
              {t('Connect')}
            </CtyButton>
          ) : (
            <CtyButton size="sm" variant="light" color="danger" onPress={reloadTemplates}>
              {t('Retry')}
            </CtyButton>
          )}
        </div>
      )}

      <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-hidden rounded-lg border border-divider bg-content1">
        <style>{HORIZONTAL_RULES_AG_GRID_CSS}</style>
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70">
            <CtySpinner size="lg" />
          </div>
        )}
        {/* Fixed height rather than autoHeight, so the pagination panel stays at the bottom
            of the card instead of riding up under the last row. */}
        <div className="doc-settings-hrules-ag-grid ag-theme-alpine h-full min-w-[760px]">
          <AgGridReact<InspectionTemplate>
            ref={gridRef}
            rowData={templates}
            columnDefs={COLUMN_DEFS}
            context={gridContext}
            getRowId={({ data }) => data.id}
            defaultColDef={{ sortable: true, resizable: true, suppressMovable: true }}
            headerHeight={44}
            rowHeight={54}
            pagination
            paginationPageSize={10}
            paginationPageSizeSelector={[10, 20, 50, 100]}
            suppressCellFocus
            overlayNoRowsTemplate={t('No inspection templates found')}
          />
        </div>
      </div>

      <InspectionTemplateEditModal
        template={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => {
          setEditTarget(null);
          reloadTemplates();
        }}
      />

      <SimpleDeleteConfirmationModal
        isOpen={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setDeleteTarget(null);
        }}
        entityName={deleteTarget?.name}
        onConfirm={handleDelete}
        isConfirmLoading={isDeleting}
        confirmLabel={t('Delete Template')}
      />
    </div>
  );
}
